package org.vreo.devicehub.features.core.data.device.networking

import kotlin.io.encoding.Base64
import io.ktor.client.HttpClient
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.URLProtocol
import io.ktor.http.contentType
import io.ktor.http.path

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.data.protocolBuffer.OutgoingProtoBufMessage
import org.vreo.devicehub.features.core.data.protocolBuffer.localModelToProtoBufMessage
import org.vreo.devicehub.features.core.data.protocolBuffer.models.ProtoBufRequestType
import org.vreo.devicehub.features.core.data.protocolBuffer.protoBufMessageToLocalModel
import org.vreo.devicehub.features.core.data.safeCall
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.CameraResolution
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoConfig
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoFrameRotation
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputConfig
import org.vreo.devicehub.features.core.domain.device.webRtc.WebRtcOfferAnswer
import org.vreo.devicehub.features.core.data.device.dto.DirectDeviceRequestDto
import org.vreo.devicehub.features.core.data.device.dto.DirectMessageResponseDto
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.ProductType
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceControl
import org.vreo.devicehub.features.core.domain.device.imageRequest.StaticImageRequest
import org.vreo.devicehub.features.core.domain.device.roi.Roi
import org.vreo.devicehub.features.core.domain.device.device.anprDeviceSettings.AnprDeviceSettings
import org.vreo.devicehub.features.core.domain.device.singleShootAutoFocus.SingleShotAutoFocusConfig


/**
 * Ktor remote device control using protocol buffer messages. Allows to send and request data
 * from target device
 *
 * @property httpClient The HTTP client used to send requests.
 * @property hostUrl The URL of the host.
 * @property deviceAccessUrl The URL of the device access.
 * @property urlProtocol The URL protocol.
 */
class KtorRemoteDeviceControl(
    private val httpClient: HttpClient,
    private val hostUrl: String,
    private val deviceAccessUrl: String,
    private val urlProtocol: URLProtocol = URLProtocol.HTTPS
) : RemoteDeviceControl {
    override suspend fun setVideoConfig(
        token: String,
        deviceId: String,
        config: VideoConfig
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.VideoConfigurationMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    override suspend fun getVideoConfig(
        token: String,
        deviceId: String
    ): Result<VideoConfig, DataError.Remote> {
        // Encode configuration to protobuf message bytes, configuration is redundant and can be
        // ignored
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.VideoConfigurationMessage(
                VideoConfig(
                    resolution = CameraResolution.FOUR_K,
                    frameRotation = VideoFrameRotation.ROTATE_0,
                    targetBrightness = 0.0f,
                    maxExposureTimeUs = 0.0f,
                    minExposureTimeUs = 0.0f,
                    maxGain = 0.0f,
                    minGain = 0.0f
                )
            )
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(
                            decodedMessage.error.toRemote()
                        )
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.VideoConfigurationMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun setVrmConfiguration(
        token: String,
        deviceId: String,
        config: List<VrmOutputConfig>
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.VrmConfigurationMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    override suspend fun getVrmConfiguration(
        token: String,
        deviceId: String,
    ): Result<List<VrmOutputConfig>, DataError.Remote> {
        // Encode configuration to protobuf message bytes, configuration is redundant and can be
        // ignored
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.VrmConfigurationMessage(
                emptyList()
            )
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(
                            decodedMessage.error.toRemote()
                        )
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.VrmConfigurationMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun getWebRtcOffer(
        token: String,
        deviceId: String,
        config: WebRtcOfferAnswer
    ): Result<WebRtcOfferAnswer, DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.WebRtcAnswerMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(
                            decodedMessage.error.toRemote()
                        )
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.WebRtcAnswerMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun setRoiConfiguration(
        token: String,
        deviceId: String,
        config: List<Roi>
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.RoiListMessage(config)
        )

        // Encode protobuf message to base64 string
        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    override suspend fun getRoiConfiguration(
        token: String,
        deviceId: String
    ): Result<List<Roi>, DataError.Remote> {
        // Encode configuration to protobuf message bytes, configuration is redundant and can be
        // ignored
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.RoiListMessage(emptyList())
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(
                            decodedMessage.error.toRemote()
                        )
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.RoiListMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun getStaticImage(
        token: String,
        deviceId: String,
        config: StaticImageRequest
    ): Result<StaticImageRequest, DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.StaticImageMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(
                            decodedMessage.error.toRemote()
                        )
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.StaticImageMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun getDeviceConfiguration(
        token: String,
        deviceId: String
    ): Result<AnprDevice, DataError> {
        // Encode configuration to protobuf message bytes, configuration is redundant and can be
        // ignored
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.DeviceConfigurationMessage(
                AnprDevice(
                    deviceId = "",
                    device = ProductType.VoltS1PoleShortRange,
                    serialNumber = "",
                    softwareVersion = "",
                    ocrVersion = "",
                    localIpAddress = "",
                    macAddress = "",
                    active = true,
                    linkinPassword = "not_required",
                    primaryConnectionKey = "not_required"
                )
            )
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(decodedMessage.error.toRemote())
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.DeviceConfigurationMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun setDeviceSettings(
        token: String,
        deviceId: String,
        config: AnprDeviceSettings
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.DeviceSettingsMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    override suspend fun getDeviceSettings(
        token: String,
        deviceId: String
    ): Result<AnprDeviceSettings, DataError.Remote> {
        // Encode configuration to protobuf message bytes, configuration is redundant and can be
        // ignored
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.GET,
            message = OutgoingProtoBufMessage.DeviceSettingsMessage(AnprDeviceSettings())
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val requestResult = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        // If successful, convert response data to local data model
        return when (requestResult) {
            is Result.Success -> {
                // Get message from response as local data model
                val message = try {
                    val decodedMessage = protoBufMessageToLocalModel(
                        byteArray = Base64.decode(requestResult.data.first().message)
                    )

                    when (decodedMessage) {
                        is Result.Success -> decodedMessage.data
                        is Result.Error -> return Result.Error(decodedMessage.error.toRemote())
                    }
                } catch (_: Exception) {
                    return Result.Error(DataError.Remote.SERIALIZATION)
                }

                if (message is OutgoingProtoBufMessage.DeviceSettingsMessage) {
                    Result.Success(message.data)
                } else {
                    Result.Error(DataError.Remote.INVALID_ARGUMENT)
                }
            }

            is Result.Error -> {
                Result.Error(requestResult.error)
            }
        }
    }

    override suspend fun setSingleShotAutoFocus(
        token: String,
        deviceId: String,
        config: SingleShotAutoFocusConfig
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.SingleShotAutofocusMessage(config)
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendDirectDeviceMessage(
            token = token,
            request = DirectDeviceRequestDto(
                deviceId = deviceId,
                messages = listOf(Base64.encode(encodedPayload))
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    private suspend fun sendDirectDeviceMessage(
        token: String,
        request: DirectDeviceRequestDto
    ): Result<List<DirectMessageResponseDto>, DataError.Remote> {
        // Send request to device
        return runCatching {
            safeCall<List<DirectMessageResponseDto>>(
                execute = {
                    httpClient.post {
                        url {
                            protocol = urlProtocol
                            host = hostUrl
                            path(deviceAccessUrl)
                        }
                        bearerAuth(token)
                        contentType(ContentType.Application.Json)
                        setBody(request)
                    }
                }
            )
        }.getOrElse { error ->
            AppLogger.error("Failed to send direct device message, error: ${error.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }
}